import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Chip,
  TextField,
  MenuItem,
  Link,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Divider,
  Tooltip,
  Select,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  List,
  ListItem,
  ListItemText,
} from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import RefreshIcon from '@mui/icons-material/Refresh'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import TouchAppIcon from '@mui/icons-material/TouchApp'
import {
  jobOfferService,
  driverService,
  shipmentService,
  accountService,
  JobOfferDto,
  ShipmentDto,
  DriverDto,
  AccountDto,
} from '../api/adminServices'
import { ResizableTableCell, stickyActionsHeaderSx, stickyActionsBodySx } from '../components/ResizableTable'
import { useLocalizedLabel } from '../utils/getLocalizedLabel'
import { useToast } from '../components/useToast'
import { EmptyState, ErrorCard, TableSkeleton } from '../components/Feedback'
import ConfirmDialog from '../components/ConfirmDialog'
import PriceBreakdownModal from '../components/PriceBreakdownModal'
import AccountDetailDialog from '../components/AccountDetailDialog'
import { AccountNameLink } from '../components/EntityAccountCell'
import { PropertyView, PropertySection, PropertyField } from '../components/PropertyView'
import {
  extractJobOfferPriceBreakdown,
  formatBreakdownSummary,
  PriceBreakdownData,
} from '../utils/priceBreakdown'

type TimeWindow = 'LAST_30_DAYS' | 'TODAY' | 'NEXT_7_DAYS' | 'NEXT_30_DAYS'

const STATUS_OPTIONS = ['OPEN', 'PLANNED', 'EXPIRED', 'REJECTED'] as const
type JobOfferStatus = (typeof STATUS_OPTIONS)[number]
type JobOfferStatusFilter = JobOfferStatus | ''

function accountDisplayName(
  accountId: string | undefined,
  accountById: Map<string, AccountDto>
): string {
  if (!accountId) return '—'
  const account = accountById.get(accountId)
  return account?.businessName || `${accountId.substring(0, 10)}…`
}

function getFreighterAccountId(offer: JobOfferDto): string | undefined {
  return offer.accountId ?? offer.freighterAccountId
}

function getUniqueShipperAccountIds(offer: JobOfferDto): string[] {
  const shipments = Array.isArray(offer.shipments) ? offer.shipments : []
  const ids = new Set<string>()
  shipments.forEach((shipment) => {
    if (shipment.shipperAccountId) ids.add(shipment.shipperAccountId)
  })
  return [...ids]
}

interface ShippersListDialogProps {
  open: boolean
  shipperAccountIds: string[]
  accountById: Map<string, AccountDto>
  onClose: () => void
  onOpenAccount?: (accountId: string) => void
}

function ShippersListDialog({
  open,
  shipperAccountIds,
  accountById,
  onClose,
  onOpenAccount,
}: ShippersListDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        Shippers ({shipperAccountIds.length})
      </DialogTitle>
      <DialogContent dividers>
        <List dense disablePadding>
          {shipperAccountIds.map((accountId) => (
            <ListItem key={accountId} disableGutters>
              <ListItemText
                primary={
                  <AccountNameLink
                    accountId={accountId}
                    accountById={accountById}
                    onOpenAccount={onOpenAccount}
                  />
                }
                secondary={accountId}
                secondaryTypographyProps={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
              />
            </ListItem>
          ))}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

interface ShipperAccountsCellProps {
  offer: JobOfferDto
  accountById: Map<string, AccountDto>
  onShowShippers: (accountIds: string[]) => void
  onOpenAccount?: (accountId: string) => void
}

function ShipperAccountsCell({
  offer,
  accountById,
  onShowShippers,
  onOpenAccount,
}: ShipperAccountsCellProps) {
  const shipperAccountIds = getUniqueShipperAccountIds(offer)

  if (shipperAccountIds.length === 0) {
    return <span>—</span>
  }

  if (shipperAccountIds.length === 1) {
    return (
      <AccountNameLink
        accountId={shipperAccountIds[0]}
        accountById={accountById}
        onOpenAccount={onOpenAccount}
      />
    )
  }

  const count = shipperAccountIds.length
  return (
    <Link
      component="button"
      variant="body2"
      onClick={(e) => {
        e.stopPropagation()
        onShowShippers(shipperAccountIds)
      }}
      sx={{ fontWeight: 600 }}
    >
      {count} shippers
    </Link>
  )
}

const TIME_WINDOW_LABELS: Record<TimeWindow, string> = {
  LAST_30_DAYS: 'Last 30 Days',
  TODAY: 'Today',
  NEXT_7_DAYS: 'Next 7 Days',
  NEXT_30_DAYS: 'Next 30 Days',
}

function statusColor(
  status: string
): 'primary' | 'success' | 'error' | 'warning' | 'info' | 'default' {
  switch (String(status).toUpperCase()) {
    case 'OPEN': return 'primary'
    case 'PLANNED': return 'info'
    case 'REJECTED': return 'error'
    case 'EXPIRED': return 'warning'
    default: return 'default'
  }
}

function formatToIsoLocalDateTime(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`
}

function getDateRange(window: TimeWindow): { from: string; to: string } {
  const now = new Date()
  let from: Date
  let to: Date

  switch (window) {
    case 'LAST_30_DAYS':
      from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30, 0, 0, 0)
      to = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
      break
    case 'TODAY':
      from = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0)
      to = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
      break
    case 'NEXT_7_DAYS':
      from = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0)
      to = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 7, 23, 59, 59)
      break
    case 'NEXT_30_DAYS':
      from = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0)
      to = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 30, 23, 59, 59)
      break
    default:
      from = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0)
      to = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)
  }

  return {
    from: formatToIsoLocalDateTime(from),
    to: formatToIsoLocalDateTime(to),
  }
}

interface DetailDialogProps {
  open: boolean
  offer: JobOfferDto | null
  onClose: () => void
  onAccepted: () => void
  onRejected: () => void
  onOpenPriceBreakdown: (title: string, breakdown: PriceBreakdownData) => void
  onShowShippers: (accountIds: string[]) => void
  onOpenAccount?: (accountId: string) => void
  getLocalizedLabel: (enumName: string, code: string | null | undefined) => string
  accountById: Map<string, AccountDto>
}

function DetailDialog({
  open,
  offer,
  onClose,
  onAccepted,
  onRejected,
  onOpenPriceBreakdown,
  onShowShippers,
  onOpenAccount,
  getLocalizedLabel,
  accountById,
}: DetailDialogProps) {
  const [tab, setTab] = useState(0)
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false)
  const [selectedDriverId, setSelectedDriverId] = useState('')
  const [rejectConfirmOpen, setRejectConfirmOpen] = useState(false)
  const toast = useToast()
  const queryClient = useQueryClient()

  const freighterAccountId = offer ? getFreighterAccountId(offer) : undefined
  const embeddedShipments = offer && Array.isArray(offer.shipments) ? offer.shipments : []

  const { data: fetchedShipments, isLoading: shipmentsLoading } = useQuery({
    queryKey: ['job-offer-shipments', offer?.id],
    queryFn: () => jobOfferService.getShipments(offer!.id),
    enabled: open && !!offer && tab === 1 && embeddedShipments.length === 0,
  })

  const shipments = embeddedShipments.length > 0 ? embeddedShipments : (fetchedShipments ?? [])

  const { data: drivers, isLoading: driversLoading } = useQuery({
    queryKey: ['drivers-for-offer', freighterAccountId],
    queryFn: () => driverService.getAllByAccount(freighterAccountId!),
    enabled: acceptDialogOpen && !!freighterAccountId,
  })

  const acceptMutation = useMutation({
    mutationFn: ({ id, driverId }: { id: string; driverId: string }) =>
      jobOfferService.accept(id, driverId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-offers'] })
      setAcceptDialogOpen(false)
      setSelectedDriverId('')
      toast.success('Job offer accepted successfully')
      onAccepted()
    },
    onError: (e) => toast.errorFrom(e, 'Failed to accept job offer'),
  })

  const rejectMutation = useMutation({
    mutationFn: (id: string) => jobOfferService.reject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-offers'] })
      setRejectConfirmOpen(false)
      toast.success('Job offer rejected')
      onRejected()
    },
    onError: (e) => toast.errorFrom(e, 'Failed to reject job offer'),
  })

  if (!offer) return null

  const priceBreakdown = extractJobOfferPriceBreakdown(offer, offer.currency)

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6">Job Offer Details</Typography>
            <Chip
              label={getLocalizedLabel('jobOfferStatus', offer.status) || offer.status}
              color={statusColor(offer.status)}
              size="small"
            />
          </Box>
        </DialogTitle>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Details" />
          <Tab label="Shipments" />
        </Tabs>

        <DialogContent>
          {tab === 0 && (
            <Box sx={{ mt: 1 }}>
              <PropertyView>
                <PropertySection title="References">
                  <PropertyField label="Offer ID" value={offer.id} mono copyable full />
                  <PropertyField label="Shipment Request ID" value={offer.shipmentRequestId} mono copyable />
                  <PropertyField label="Shipment ID" value={offer.shipmentId} mono copyable />
                  <PropertyField label="Freighter" value={freighterAccountId} mono copyable>
                    <AccountNameLink
                      accountId={freighterAccountId}
                      accountById={accountById}
                      onOpenAccount={onOpenAccount}
                    />
                  </PropertyField>
                  <PropertyField label="Shippers">
                    <ShipperAccountsCell
                      offer={offer}
                      accountById={accountById}
                      onShowShippers={onShowShippers}
                      onOpenAccount={onOpenAccount}
                    />
                  </PropertyField>
                  <PropertyField label="Vehicle ID" value={offer.vehicleId} mono copyable />
                </PropertySection>
                <PropertySection title="Terms">
                  <PropertyField label="Offered Price">
                    {priceBreakdown ? (
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => onOpenPriceBreakdown('Offered Price', priceBreakdown)}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          fontWeight: 600,
                        }}
                      >
                        <TouchAppIcon sx={{ fontSize: 16, opacity: 0.8 }} />
                        {formatBreakdownSummary(priceBreakdown, (code) => getLocalizedLabel('currency', code))}
                      </Link>
                    ) : offer.price != null ? (
                      `${offer.price.toLocaleString()} ${offer.currency || ''}`.trim()
                    ) : (
                      '—'
                    )}
                  </PropertyField>
                  <PropertyField label="Estimated Delivery" value={offer.estimatedDeliveryTime} type="datetime" />
                  <PropertyField label="Expires At" value={offer.expiresAt} type="datetime" />
                  <PropertyField label="Created At" value={offer.createdAt} type="datetime" />
                </PropertySection>
              </PropertyView>

              {offer.status === 'OPEN' && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="subtitle2" gutterBottom>Admin Actions</Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircleIcon />}
                      onClick={() => setAcceptDialogOpen(true)}
                    >
                      Accept Offer
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<CancelIcon />}
                      onClick={() => setRejectConfirmOpen(true)}
                    >
                      Reject Offer
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          )}

          {tab === 1 && (
            <Box sx={{ mt: 1 }}>
              {shipmentsLoading ? (
                <Box display="flex" justifyContent="center" p={4}>
                  <CircularProgress />
                </Box>
              ) : !shipments || shipments.length === 0 ? (
                <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                  No shipments linked to this job offer
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Shipment ID</TableCell>
                        <TableCell>Shipper</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Pickup</TableCell>
                        <TableCell>Delivery</TableCell>
                        <TableCell>Price</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(shipments as ShipmentDto[]).map((s) => (
                        <TableRow key={s.id}>
                          <TableCell>
                            <Tooltip title={s.id}>
                              <span>{s.id.substring(0, 10)}...</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell
                            sx={{
                              maxWidth: 180,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <AccountNameLink
                              accountId={s.shipperAccountId}
                              accountById={accountById}
                              onOpenAccount={onOpenAccount}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getLocalizedLabel('shipmentStatus', s.status) || s.status}
                              size="small"
                            />
                          </TableCell>
                          <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {s.pickupAddress || '-'}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {s.cargos && s.cargos.length > 0
                              ? s.cargos.map((c: any) => c.deliveryAddress).filter(Boolean).join('; ') || '-'
                              : '-'}
                          </TableCell>
                          <TableCell>
                            {s.offeredPrice != null ? `${s.offeredPrice} ${s.currency || ''}` : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={acceptDialogOpen} onClose={() => setAcceptDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Accept Job Offer</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a driver from the freighter account to assign to this offer.
          </Typography>
          {driversLoading ? (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel>Select Driver</InputLabel>
              <Select
                value={selectedDriverId}
                onChange={(e) => setSelectedDriverId(e.target.value)}
                label="Select Driver"
              >
                <MenuItem value="">-- Select a Driver --</MenuItem>
                {(drivers as DriverDto[] || []).map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.user?.name || d.licenseNo} — {d.user?.status || '—'}
                    {' · '}
                    {d.id.substring(0, 8)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAcceptDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            disabled={!selectedDriverId || acceptMutation.isPending}
            onClick={() => acceptMutation.mutate({ id: offer.id, driverId: selectedDriverId })}
          >
            {acceptMutation.isPending ? 'Accepting...' : 'Confirm Accept'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={rejectConfirmOpen}
        title="Reject Job Offer"
        description="Are you sure you want to reject this job offer? This cannot be undone."
        confirmLabel="Reject"
        severity="error"
        loading={rejectMutation.isPending}
        onClose={() => setRejectConfirmOpen(false)}
        onConfirm={() => rejectMutation.mutate(offer.id)}
      />
    </>
  )
}

export default function JobOffersPage() {
  const getLocalizedLabel = useLocalizedLabel()
  const [timeWindow, setTimeWindow] = useState<TimeWindow>('TODAY')
  const [statusFilter, setStatusFilter] = useState<JobOfferStatusFilter>('OPEN')
  const [freighterFilter, setFreighterFilter] = useState('')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [selectedOffer, setSelectedOffer] = useState<JobOfferDto | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [priceModal, setPriceModal] = useState<{
    open: boolean
    title: string
    breakdown: PriceBreakdownData | null
  }>({ open: false, title: '', breakdown: null })
  const [shippersModal, setShippersModal] = useState<{
    open: boolean
    accountIds: string[]
  }>({ open: false, accountIds: [] })
  const [accountDetailId, setAccountDetailId] = useState<string | null>(null)

  const hasCustomDateRange = Boolean(customFrom || customTo)

  const dateRange = useMemo(() => {
    if (!hasCustomDateRange) {
      return getDateRange(timeWindow)
    }

    const now = new Date()
    const defaultFrom = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30, 0, 0, 0)
    const defaultTo = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59)

    const from = customFrom
      ? new Date(`${customFrom}T00:00:00`)
      : defaultFrom
    const to = customTo
      ? new Date(`${customTo}T23:59:59`)
      : defaultTo

    return {
      from: formatToIsoLocalDateTime(from),
      to: formatToIsoLocalDateTime(to),
    }
  }, [customFrom, customTo, hasCustomDateRange, timeWindow])

  const { data: accountsData } = useQuery({
    queryKey: ['accounts-for-job-offers'],
    queryFn: () => accountService.getAll(0, 200),
  })

  const accounts: AccountDto[] = useMemo(
    () => accountsData?.content ?? accountsData ?? [],
    [accountsData]
  )

  const accountById = useMemo(() => {
    const map = new Map<string, AccountDto>()
    accounts.forEach((account) => map.set(account.id, account))
    return map
  }, [accounts])

  const freighterAccounts = useMemo(
    () => accounts.filter((a) => a.type === 'FREIGHTER'),
    [accounts]
  )

  const { data: allOffers, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['job-offers', dateRange.from, dateRange.to],
    queryFn: async () => {
      const result = await shipmentService.getJobOffersByTimeWindow(dateRange.from, dateRange.to)
      return Array.isArray(result) ? result : result?.content || result?.data || []
    },
    retry: 1,
  })

  const filteredOffers = ((allOffers as JobOfferDto[]) || []).filter((o) => {
    if (statusFilter && String(o.status || '').toUpperCase() !== statusFilter) {
      return false
    }
    if (freighterFilter && getFreighterAccountId(o) !== freighterFilter) {
      return false
    }
    return true
  })

  const totalInWindow = (allOffers as JobOfferDto[] | undefined)?.length ?? 0
  const statusLabel = statusFilter
    ? getLocalizedLabel('jobOfferStatus', statusFilter) || statusFilter
    : 'All'
  const freighterLabel = freighterFilter
    ? accountDisplayName(freighterFilter, accountById)
    : 'All'
  const dateRangeLabel = hasCustomDateRange
    ? `${customFrom || '…'} → ${customTo || '…'}`
    : TIME_WINDOW_LABELS[timeWindow]

  const handleTimeWindowChange = (_event: React.MouseEvent<HTMLElement>, newValue: TimeWindow | null) => {
    if (newValue !== null) {
      setTimeWindow(newValue)
      setCustomFrom('')
      setCustomTo('')
    }
  }

  const handleView = (offer: JobOfferDto) => {
    setSelectedOffer(offer)
    setDetailOpen(true)
  }

  const handleCloseDetail = () => {
    setDetailOpen(false)
    setSelectedOffer(null)
  }

  const handleShowShippers = (accountIds: string[]) => {
    setShippersModal({ open: true, accountIds })
  }

  const renderJobOfferPrice = (offer: JobOfferDto) => {
    const breakdown = extractJobOfferPriceBreakdown(offer, offer.currency)
    if (breakdown) {
      return (
        <Link
          component="button"
          variant="body2"
          onClick={(e) => {
            e.stopPropagation()
            setPriceModal({ open: true, title: 'Offered Price', breakdown })
          }}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            fontWeight: 600,
          }}
        >
          <TouchAppIcon sx={{ fontSize: 16, opacity: 0.8 }} />
          {formatBreakdownSummary(breakdown, (code) => getLocalizedLabel('currency', code))}
        </Link>
      )
    }
    if (offer.price != null) {
      return `${offer.price.toLocaleString()} ${offer.currency || ''}`.trim()
    }
    return '-'
  }

  return (
    <Box className="animate-fade-in" sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Time Window
          </Typography>
          <ToggleButtonGroup
            value={hasCustomDateRange ? null : timeWindow}
            exclusive
            onChange={handleTimeWindowChange}
            aria-label="time window filter"
            size="small"
            sx={{ mb: 2 }}
          >
            <ToggleButton value="LAST_30_DAYS" aria-label="last 30 days">
              Last 30 Days
            </ToggleButton>
            <ToggleButton value="TODAY" aria-label="today">
              Today
            </ToggleButton>
            <ToggleButton value="NEXT_7_DAYS" aria-label="next 7 days">
              Next 7 Days
            </ToggleButton>
            <ToggleButton value="NEXT_30_DAYS" aria-label="next 30 days">
              Next 30 Days
            </ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Custom Date Range
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              type="date"
              label="From"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              type="date"
              label="To"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            {hasCustomDateRange && (
              <Button
                size="small"
                onClick={() => {
                  setCustomFrom('')
                  setCustomTo('')
                }}
              >
                Clear dates
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      <Paper sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center',
          }}
        >
          <TextField
            select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as JobOfferStatusFilter)}
            sx={{ minWidth: 200 }}
            size="small"
          >
            <MenuItem value="">All</MenuItem>
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>
                {getLocalizedLabel('jobOfferStatus', s) || s}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Freighter"
            value={freighterFilter}
            onChange={(e) => setFreighterFilter(e.target.value)}
            sx={{ minWidth: 200 }}
            size="small"
          >
            <MenuItem value="">All freighters</MenuItem>
            {freighterAccounts.map((a) => (
              <MenuItem key={a.id} value={a.id}>
                {a.businessName || `${a.id.substring(0, 8)}…`}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flex: 1 }}>
            <FilterAltIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
              Active filters:
            </Typography>
            <Chip label={`Date: ${dateRangeLabel}`} size="small" color="primary" variant="outlined" />
            <Chip label={`Status: ${statusLabel}`} size="small" color="primary" variant="outlined" />
            {freighterFilter && (
              <Chip label={`Freighter: ${freighterLabel}`} size="small" color="primary" variant="outlined" />
            )}
          </Box>

          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={() => refetch()} disabled={isFetching} size="small">
                {isFetching ? <CircularProgress size={20} /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>

          <Typography variant="body2" color="text.secondary">
            {filteredOffers.length} of {totalInWindow} in window
          </Typography>
        </Box>

        {isLoading ? (
          <Box sx={{ flex: 1, minHeight: 0, p: 2 }}>
            <TableSkeleton rows={8} columns={7} />
          </Box>
        ) : isError ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
            <ErrorCard
              title="Failed to load job offers"
              message={(error as Error)?.message}
              onRetry={() => refetch()}
            />
          </Box>
        ) : filteredOffers.length === 0 ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
            <EmptyState
              title={
                totalInWindow === 0
                  ? 'No job offers found'
                  : 'No job offers match the current filters'
              }
              description={
                totalInWindow === 0
                  ? `No job offers in the selected date range (${dateRangeLabel}).`
                  : statusFilter || freighterFilter
                    ? 'Try adjusting the status, freighter, or date range filters.'
                    : 'Try a different date range.'
              }
            />
          </Box>
        ) : (
          <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <ResizableTableCell>Freighter</ResizableTableCell>
                  <ResizableTableCell>Shipper</ResizableTableCell>
                  <ResizableTableCell>Status</ResizableTableCell>
                  <ResizableTableCell>Offered Price</ResizableTableCell>
                  <ResizableTableCell>Expires At</ResizableTableCell>
                  <ResizableTableCell>Created</ResizableTableCell>
                  <ResizableTableCell sx={stickyActionsHeaderSx}>Actions</ResizableTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredOffers.map((offer: JobOfferDto) => (
                  <TableRow key={offer.id}>
                    <TableCell
                      sx={{
                        maxWidth: 180,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <AccountNameLink
                        accountId={getFreighterAccountId(offer)}
                        accountById={accountById}
                        onOpenAccount={setAccountDetailId}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 180,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <ShipperAccountsCell
                        offer={offer}
                        accountById={accountById}
                        onShowShippers={handleShowShippers}
                        onOpenAccount={setAccountDetailId}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getLocalizedLabel('jobOfferStatus', offer.status) || offer.status}
                        color={statusColor(offer.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{renderJobOfferPrice(offer)}</TableCell>
                    <TableCell>
                      {offer.expiresAt
                        ? new Date(offer.expiresAt).toLocaleString()
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {new Date(offer.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell sx={stickyActionsBodySx}>
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => handleView(offer)}>
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <DetailDialog
        open={detailOpen}
        offer={selectedOffer}
        onClose={handleCloseDetail}
        onAccepted={handleCloseDetail}
        onRejected={handleCloseDetail}
        onOpenPriceBreakdown={(title, breakdown) =>
          setPriceModal({ open: true, title, breakdown })
        }
        onShowShippers={handleShowShippers}
        onOpenAccount={setAccountDetailId}
        getLocalizedLabel={getLocalizedLabel}
        accountById={accountById}
      />

      <ShippersListDialog
        open={shippersModal.open}
        shipperAccountIds={shippersModal.accountIds}
        accountById={accountById}
        onClose={() => setShippersModal({ open: false, accountIds: [] })}
        onOpenAccount={setAccountDetailId}
      />

      <AccountDetailDialog
        accountId={accountDetailId}
        open={!!accountDetailId}
        onClose={() => setAccountDetailId(null)}
      />

      <PriceBreakdownModal
        open={priceModal.open}
        title={priceModal.title}
        breakdown={priceModal.breakdown}
        onClose={() => setPriceModal({ open: false, title: '', breakdown: null })}
      />
    </Box>
  )
}
